/**
 * Teach a construct at first meeting (stitching review 5.5;
 * docs/the-story.md, rule 2).
 *
 * The definitions already existed; what did not was any memory of
 * whether a person had met them, so a construct was either explained
 * on every visit or not at all. This shows each term's plain sentence,
 * from the one glossary definition, until the person says they have it;
 * after that it is gone from the page and stays in the glossary.
 *
 * The record is per person on the server, so it follows them across
 * devices. Until it has loaded nothing is shown, so a term already met
 * never flashes.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useMeetTerm, useUserPreferences } from "shared/api/userPreferences";
import { GLOSSARY } from "shared/glossary/terms";
import { recordActedOn, recordShown, shownBefore } from "shared/glossary/teachOnce";

export function FirstMeetings({
  terms,
  className,
}: {
  /** Glossary keys met on this surface, in the order to teach them. */
  terms: readonly string[];
  className?: string;
}) {
  const { t } = useTranslation();
  const prefs = useUserPreferences();
  const meet = useMeetTerm();
  const [gotHere, setGotHere] = useState<string[]>([]);
  // Round 18 G29 -- the terms this surface is first to show; any other
  // surface showing one later in the visit is quiet.
  const [owned] = useState(() => new Set(terms.filter((k) => !shownBefore(k))));
  const ready = Boolean(prefs.data);
  const metNow = new Set([...(prefs.data?.met_terms ?? []), ...gotHere]);
  const unmet = terms.filter(
    (k) => !metNow.has(k) && GLOSSARY[k] !== undefined && owned.has(k),
  );
  const unmetKey = unmet.join("|");
  useEffect(() => {
    if (!ready) return;
    for (const k of unmetKey.split("|").filter(Boolean)) recordShown(k);
  }, [ready, unmetKey]);
  if (!prefs.data) return null;
  if (unmet.length === 0) return null;
  return (
    <section
      data-testid="first-meetings"
      aria-label={t("glossary.firstMeeting.label")}
      className={`space-y-2 rounded-lg border border-brand-brassLine bg-brand-brassTint px-3 py-2.5 ${className ?? ""}`.trim()}
    >
      {unmet.map((key) => {
        const term = GLOSSARY[key]!;
        return (
          <div
            key={key}
            data-testid={`first-meeting-${key}`}
            dir="auto"
            className="text-sm text-ink"
          >
            <p>
              <span className="font-semibold">{term.term}.</span> {term.short}
            </p>
            <p className="mt-1 flex flex-wrap gap-x-3 text-xs">
              <Link
                to={`/glossary#${key}`}
                onClick={() => recordActedOn(key)}
                className="text-brand-brassDark hover:underline"
              >
                {t("glossary.learnMore")}
              </Link>
              <button
                type="button"
                data-testid={`first-meeting-got-it-${key}`}
                onClick={() => {
                  setGotHere((g) => [...g, key]);
                  meet.mutate(key);
                }}
                className="font-medium text-brand-brassDark hover:underline"
              >
                {t("glossary.firstMeeting.gotIt")}
              </button>
            </p>
          </div>
        );
      })}
    </section>
  );
}
